import type { ShiftRQSchema } from '@libs/data-access/queries'
import { Badge, IconWrap, MealIcon, Tooltip } from '@libs/ui/ds'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import classnames from 'classnames'
import React from 'react'
import type { FC } from 'react'

import { useReactBlockHoursStyles } from '../../hooks/useReactBlockHoursStyles'
import { BreakCol } from '../BreakCol/BreakCol'
import { hasMealsToShow, MealCol } from '../MealCol/MealCol'
import { Note } from '../Note/Note'
import styles from '../styles/ShiftRealHoursCardsStyle.module.css'

type Props = {
  shift: ShiftRQSchema
  color?: string
  restType?: { value: string; label: string }
  restNote?: string
  didNotShow?: boolean
  hasRealCard: boolean
  totalRealBreakTime: number
  shiftIsFinishedWithoutOut: boolean
  witheldFromPay?: boolean
  isShiftValid: boolean
}
export const RealCard: FC<Props> = ({
  color,
  shift,
  restType = null,
  restNote = null,
  didNotShow,
  hasRealCard,
  totalRealBreakTime,
  shiftIsFinishedWithoutOut,
  witheldFromPay,
  isShiftValid
}) => {
  const { t, tCommon, tDates } = usePlanningFormTranslation()

  const reactBlockStyle = useReactBlockHoursStyles({ color })

  const getTotalMeal = shift.shift_meals.reduce(
    (acc, nextValue) =>
      (acc ?? 0) +
      Number(
        nextValue?.real_meal_type !== null && nextValue?.real_meal_type !== 'NT'
      ),
    0
  )

  const rangeDates = tDates('range.shortHourMinuteDashed', {
    start: shift.real_starts_at,
    end: shift.real_ends_at
  })

  return (
    <div
      className={classnames(styles.BreakRow, styles.BreakSeparator)}
      id='real-card'
    >
      <div className={styles.TitleCard}>
        {t('shiftRealHoursForm.realCard.title')}
      </div>
      <div className={styles.BreakCol}>
        {didNotShow && (
          <>
            <span className={styles.BadgeAndHours}>
              <span className={styles.DidntCameText}>
                {t('shiftRealHoursForm.realCard.didNotCome')}
              </span>
              {restType && <Badge label={restType.label} size='s' />}
            </span>
            {!!shift?.real_note && (
              <div className={styles.CardSubPart}>
                <Note note={shift.real_note} />
              </div>
            )}
            {!!restNote && (
              <Tooltip
                id='rest-note-tip'
                position='left'
                label={t('shiftRealHoursForm.realCard.absence')}
              >
                <div className={styles.CardSubPart}>
                  <Note note={restNote} />
                </div>
              </Tooltip>
            )}
          </>
        )}
        {!didNotShow &&
          (hasRealCard ? (
            <>
              <div className={styles.BadgeAndHours}>
                <span style={reactBlockStyle} />
                {t('shiftRealHoursForm.common.breakTime', {
                  range: rangeDates,
                  total: totalRealBreakTime,
                  size: shift?.shift_meals?.length > 0 ? getTotalMeal : ''
                })}
                {shift?.shift_meals?.length > 0 && (
                  <IconWrap icon={<MealIcon />} />
                )}
              </div>
              {witheldFromPay && (
                <div className={styles.WitheldFromPay}>
                  <Badge
                    label={t('shiftRealHoursForm.realCard.payrollDeduction')}
                    color='semantic.red'
                    size='s'
                  />
                </div>
              )}
              {(totalRealBreakTime !== 0 ||
                shift?.shift_breaks?.length > 0) && (
                <div className={styles.CardSubPart}>
                  <BreakCol
                    breaks={shift.shift_breaks}
                    size={shift.shift_breaks.length}
                    type='real'
                    cardType='real'
                    shiftIsFinishedWithoutOut={shiftIsFinishedWithoutOut}
                    hasEmployeeModifyReal={Boolean(
                      shift.real_hours_last_modified_by_employee_at
                    )}
                    isShiftValid={isShiftValid}
                  />
                </div>
              )}
              {hasMealsToShow(shift.shift_meals, 'real') && (
                <div className={styles.CardSubPart}>
                  <MealCol meals={shift.shift_meals} scheduleType='real' />
                </div>
              )}
              {Boolean(shift?.real_note) && (
                <div className={styles.CardSubPart}>
                  <Note note={shift.real_note} />
                </div>
              )}
              {Boolean(restNote) && (
                <Tooltip
                  id='rest-note-tip'
                  position='left'
                  label={t('shiftRealHoursForm.realCard.absence')}
                >
                  <div className={styles.CardSubPart} />
                </Tooltip>
              )}
            </>
          ) : (
            <span className={styles.HasNoRealCard}>
              {tCommon('form.uninformed')}
            </span>
          ))}
      </div>
    </div>
  )
}
